import { makeStyles, tokens, mergeClasses } from '@coreai/fluentui-react';
import { CheckmarkCircleRegular } from '@fluentui/react-icons';
import { useAutosaveStatus } from './useAutosaveStatus';

/**
 * Ambient "saving" pulse cadence. Deliberately off the Fluent motion scale:
 * `tokens.duration*` tops out at `durationUltraSlow` (500ms), which is tuned
 * for discrete interaction feedback and pulses too aggressively for a passive,
 * always-present status breath. A slow ~1.2s cycle reads as calm and
 * non-demanding. Kept as a single named constant so it can't drift silently.
 */
const SAVING_PULSE_DURATION = '1.2s';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorPaletteGreenForeground1,
    userSelect: 'none',
    transitionProperty: 'color, opacity',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  saving: {
    animationName: {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.55 },
    },
    animationDuration: SAVING_PULSE_DURATION,
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  },
  icon: {
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
  },
});

/**
 * Small, icon-only autosave status shown in the drawer header. Reflects the
 * editor's real save lifecycle via {@link useAutosaveStatus} and announces
 * changes politely to assistive tech. It never demands attention: a persistent
 * green check that pulses while an autosave is pending.
 *
 * Copy note: no visible text — earlier "Saved to draft" wording implied a
 * durable per-edit persistence the two-tier autosave model doesn't guarantee.
 * The state-driven `aria-label` uses neutral mechanism language ("Autosave" /
 * "Autosaving") for screen-reader users so the affordance stays honest.
 */
export const NodeEditorAutosaveIndicator = () => {
  const styles = useStyles();
  const state = useAutosaveStatus();
  const ariaLabel = state === 'saving' ? 'Autosaving' : 'Autosave';

  return (
    <div
      className={mergeClasses(styles.root, state === 'saving' && styles.saving)}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      <CheckmarkCircleRegular className={styles.icon} aria-hidden />
    </div>
  );
};

NodeEditorAutosaveIndicator.displayName = 'NodeEditorAutosaveIndicator';
